import { useCallback, useEffect, useRef, useState } from "react";

/*
 * Real-time feed client.
 *
 * Connects to the server-side Calchas proxy (same-origin `/feed-ws/` behind
 * nginx by default) and exposes a coherent, always-current list of "fairs".
 * The proxy sends `status` and `snapshot` frames; this hook keeps the latest
 * snapshot and tracks which entries changed so the UI can flash them.
 */

function resolveWsUrl() {
  const override = process.env.REACT_APP_FEED_WS_URL;
  if (override) return override;
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/feed-ws`;
}

function keyOf(fair) {
  if (!fair || typeof fair !== "object") return String(fair);
  return (
    fair.key ??
    fair.book_key ??
    fair.market_key ??
    fair.id ??
    fair.theo_id ??
    JSON.stringify(fair)
  );
}

export default function useFeedSocket() {
  const [status, setStatus] = useState("connecting"); // connecting | connected | disconnected
  const [upstream, setUpstream] = useState(null); // upstream feed status from proxy
  const [fairs, setFairs] = useState([]);
  const [lastFairs, setLastFairs] = useState([]); // last-known fairs incl. retracted
  const [schedule, setSchedule] = useState([]); // full day's MLB schedule (board)
  const [changedKeys, setChangedKeys] = useState(new Set());
  const [lastUpdate, setLastUpdate] = useState(null);

  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const reconnectTimerRef = useRef(null);
  const prevMapRef = useRef(new Map());
  const manualCloseRef = useRef(false);

  const applySnapshot = useCallback((incoming) => {
    const nextMap = new Map();
    const changed = new Set();
    for (const fair of incoming) {
      const k = keyOf(fair);
      nextMap.set(k, fair);
      const prev = prevMapRef.current.get(k);
      if (!prev || JSON.stringify(prev) !== JSON.stringify(fair)) changed.add(k);
    }
    prevMapRef.current = nextMap;
    setFairs(incoming);
    setChangedKeys(changed);
    setLastUpdate(Date.now());
  }, []);

  const connect = useCallback(() => {
    const token = localStorage.getItem("token");
    const url = `${resolveWsUrl()}?token=${encodeURIComponent(token || "")}`;
    setStatus("connecting");

    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      setStatus("disconnected");
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("connected");
      backoffRef.current = 1000;
    };

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      if (msg.type === "snapshot") {
        applySnapshot(Array.isArray(msg.fairs) ? msg.fairs : []);
        if (Array.isArray(msg.lastFairs)) setLastFairs(msg.lastFairs);
        if (Array.isArray(msg.schedule)) setSchedule(msg.schedule);
        if (msg.meta) setUpstream((u) => ({ ...(u || {}), ...msg.meta }));
      } else if (msg.type === "status") {
        setUpstream(msg.upstream || null);
      }
    };

    ws.onclose = () => {
      setStatus("disconnected");
      if (!manualCloseRef.current) scheduleReconnect();
    };

    ws.onerror = () => {
      try {
        ws.close();
      } catch (e) {}
    };
  }, [applySnapshot]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return;
    const jitter = Math.random() * 0.3 * backoffRef.current;
    const delay = Math.min(backoffRef.current, 30000) + jitter;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      backoffRef.current = Math.min(backoffRef.current * 2, 30000);
      connect();
    }, delay);
  }, [connect]);

  const reconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    backoffRef.current = 1000;
    try {
      wsRef.current?.close();
    } catch (e) {}
    connect();
  }, [connect]);

  useEffect(() => {
    manualCloseRef.current = false;
    connect();
    return () => {
      manualCloseRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      try {
        wsRef.current?.close();
      } catch (e) {}
    };
  }, [connect]);

  return { status, upstream, fairs, lastFairs, schedule, changedKeys, lastUpdate, reconnect };
}
